/** Enhance My Vocals (owner session cookie): chunked upload, Original / Natural / Studio-Polished versions, re-polish, send to studio. */
import axios from "axios";
import { API } from "@/lib/creatorApi";
import { chunkedUpload } from "@/lib/chunkedUpload";

const http = axios.create({ baseURL: `${API}/enhance`, withCredentials: true });
const data = (p) => p.then((r) => r.data);

export const VERSIONS = [
  { key: "original", label: "Original", short: "A", color: "#cbd5e1", note: "Your upload, only level-matched" },
  { key: "natural", label: "Natural Voice", short: "B", color: "#34d399", note: "Minimal clean-up, your real sound" },
  { key: "polished", label: "Studio-Polished", short: "C", color: "#fbbf24", note: "Tuned, shaped, finished with space" },
];
export const LABEL = Object.fromEntries(VERSIONS.map((v) => [v.key, v.label]));
export const PHASES = {
  uploading: "Uploading", queued: "Getting ready", isolating: "Removing the music behind your voice",
  analysing: "Listening to your voice (key, range, noise)", original: "Level-matching the original",
  natural: "Making the Natural Voice version", polished: "Polishing the Studio version", polishing: "Polishing the Studio version",
  done: "Ready", error: "Stopped",
};
export const STEPS = ["isolating", "analysing", "original", "natural", "polished"];
export const NOTES = ["C", "C#", "D", "Eb", "E", "F", "F#", "G", "Ab", "A", "Bb", "B"];
export const KEYS = NOTES.flatMap((n) => [`${n} major`, `${n} minor`]);
export const ACCEPT = ".wav,.mp3,.m4a,.aac,.flac,.aif,.aiff,.ogg";
export const MAX_BYTES = 300 * 1024 * 1024;

export const PRESETS = [
  { key: "studio", label: "Studio Polished", s: { tune: "natural", eq: "balanced", compression: "medium", reverb: 25, delay: 15, doubling: false } },
  { key: "hall", label: "Worship Hall", s: { tune: "natural", eq: "warm", compression: "medium", reverb: 50, delay: 8, doubling: false } },
  { key: "rnb", label: "R&B Silk", s: { tune: "tight", eq: "bright", compression: "medium", reverb: 22, delay: 20, doubling: true } },
  { key: "reggae", label: "Reggae Echo", s: { tune: "natural", eq: "warm", compression: "medium", reverb: 20, delay: 40, doubling: false } },
  { key: "close", label: "Intimate & Close", s: { tune: "natural", eq: "warm", compression: "light", reverb: 10, delay: 0, doubling: false } },
];

export const listEnhancements = () => data(http.get(""));
export const getEnhancement = (id) => data(http.get(`/${id}`));
export const polishEnhancement = (id, body) => data(http.post(`/${id}/polish`, body));
export const retryEnhancement = (id) => data(http.post(`/${id}/retry`));
export const deleteEnhancement = (id) => data(http.delete(`/${id}`));
export const sendToStudio = (id, body) => data(http.post(`/${id}/send`, body));
export const audioUrl = (item, version, ext) => `${API}/enhance/${item.id}/audio/${version}.${ext}?v=${encodeURIComponent(item.versions?.[version]?.at || "")}`;
export const sourceUrl = (item) => `${API}/enhance/${item.id}/source`;

/** Upload in 5 MB pieces (no proxy size limit), then start processing. `onProgress(0..1)`. */
export const uploadVocal = (file, { title, isolate }, onProgress) => chunkedUpload(http, {
  start: "/uploads", chunk: (id, i) => `/uploads/${id}/chunks/${i}`, finish: (id) => `/uploads/${id}/finish`,
}, file, { filename: file.name, size: file.size, title: title || null }, { isolate: Boolean(isolate) }, onProgress);

export const fmtTime = (s) => {
  const t = Math.max(0, Math.floor(s || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};
